import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import { useActiveSessionId, useActiveSessionIsPaused } from "./useActiveSessionSlice";
import type { ActiveTaskResponse } from "../types";
import type { UseQueryOptions } from "@tanstack/react-query";
type OverrideOptions = Omit<Partial<UseQueryOptions<ActiveTaskResponse>>, "queryKey" | "queryFn">;

/**
 * Fetches the currently active task with session-aware polling.
 *
 * When an active session exists and the user is not on break, the hook polls
 * with a jittered interval (~60s +/- 10s) to avoid query storms. Polling is
 * disabled during breaks or when no session is active.
 *
 * @param options - Override any useQuery option or set `enablePolling` to
 *   control polling independently of session state.
 */
export function useActiveTask(options) {
  const $ = _c(9);
  const apiFetcher = useApiFetcher();
  const isSessionActive = Boolean(useActiveSessionId());
  const isBreakTime = useActiveSessionIsPaused();
  const enablePolling = options?.enablePolling ?? (isSessionActive && !isBreakTime);
  const refetchInterval = enablePolling ? _temp : false;
  let t0;
  if ($[0] !== apiFetcher) {
    t0 = () => apiFetcher("/api/v1/tasks/active-task");
    $[0] = apiFetcher;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !isBreakTime;
  const t2 = enablePolling ? 60000 : 300000;
  let t3;
  if ($[2] !== enablePolling || $[3] !== options || $[4] !== refetchInterval || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: QUERY_KEYS.ACTIVE_TASK,
      queryFn: t0,
      enabled: t1,
      refetchInterval,
      refetchIntervalInBackground: enablePolling,
      staleTime: t2,
      gcTime: 300000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      ...options
    };
    $[2] = enablePolling;
    $[3] = options;
    $[4] = refetchInterval;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}
function _temp() {
  return 60000 + (Math.random() * 2 - 1) * 10000;
}